import { Button } from '~/components/general/Button'
import { trackEvent } from '~/utils/tracking'
import { ResponsiveImage } from '~/components/ResponsiveImage'
import { TypeInteractiveBanner } from '~/types/contentful'
import { Asset } from 'contentful'

type BackgroundColor = 'indigo' | 'green' | 'blue'

type InteractiveBannerProps = {
  banner: TypeInteractiveBanner<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const InteractiveBanner = ({ banner }: InteractiveBannerProps) => {
  const { fields } = banner
  const {
    entryTitle,
    title,
    description,
    ctaText,
    ctaLink,
    image,
    backgroundColor = 'indigo', // Default to indigo-10
  } = fields

  const bgColors: Record<BackgroundColor, { bgColor: string; hoverBackgroundColor: string }> = {
    indigo: {
      bgColor: '#F1F2FF',
      hoverBackgroundColor: '#1A1C6A',
    },
    green: {
      bgColor: '#E7F4ED',
      hoverBackgroundColor: '#042B18',
    },
    blue: {
      bgColor: '#EAF6FF',
      hoverBackgroundColor: '#0C264C',
    },
  }

  const lowerCaseBackgroundColor = (backgroundColor?.[0]?.toLowerCase() ||
    'indigo') as BackgroundColor

  const { bgColor, hoverBackgroundColor } = bgColors[lowerCaseBackgroundColor] || bgColors.indigo

  const handleEvent = () => {
    trackEvent('click_cta', {
      target_url: ctaLink,
      banner_name: entryTitle as string,
      block: 'interactive_banner',
      cta_text: ctaText,
    })
  }
  return (
    <div
      className="group relative flex w-full flex-col justify-center items-center gap-2 rounded-xl md:flex-row md:gap-24 overflow-clip mb-12 max-md:mb-7 max-xl:mb-10"
      style={{ backgroundColor: bgColor }}
    >
      <div
        className="absolute w-full h-full opacity-0 transition-all duration-300 ease-linear group-hover:opacity-5 z-1"
        style={{ backgroundColor: hoverBackgroundColor }}
      />
      <div className="flex flex-col pt-8 px-6 gap-4 md:pl-8 md:py-12 z-2">
        <div className="flex flex-col w-full gap-2">
          <span className="text-m h-fit text-neutral-90">{title}</span>
          <p className="text-xs text-neutral-90">{description}</p>
        </div>
        <Button type={'link'} text={ctaText} href={ctaLink} onClick={handleEvent} />
      </div>
      <div className="flex flex-row justify-center items-center md:justify-end md:items-end w-fit h-fit z-0">
        <div className="h-full max-h-55 min-w-80 transition-all duration-300 ease-linear group-hover:scale-[1.135]">
          <ResponsiveImage asset={(image as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>)?.fields} />
        </div>
      </div>
    </div>
  )
}
